import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import api from 'api';
import { useNavigate } from 'react-router-dom';
import useSearchParams from 'hooks/useSearchParams';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  BODY_CONTENT_GENERATE,
  NudgeCellComponent,
} from './components/BuyerDealTableCells';
import {
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from './components/BuyerDealTableCells/data';
import { PortfoilioLead } from 'api/portfolio/buyer';
import { CUSTOM_COL_SIZES } from '../TableCells/data';
import ResolvePagination from 'components/ResolvePagination';
import ListingPortfolioCard from 'components/ListingPortfolioCard';
import PortfolioPlaceholderCards from '../../../../components/PortfolioPlaceholderCards';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import usePageParams from 'hooks/usePageParams';
import { safeStringify, scrollToTop, getIsArrayEmpty } from 'utils';
import { getApiRangesFromParams } from 'components/RangeFilterWrapper/utils';
import { BUYING_DEFAULT_SORT } from '../../../../../data';
import { BUYING_ALL_LISTINGS_QUERY } from '../../utils';
import useTableFetchState from 'hooks/useTableFetchState';
import {
  ColumnFilterOption,
  ColumnSizing,
} from 'components/ReorderResizeTable/types';

import './BuyerDeals.styles.scss';

type BuyerDealsProps = {
  className?: string;
  dataLimit: string;
  isEmpty?: boolean;
  isTableView?: boolean;
  columnFilterOptions?: Record<string, ColumnFilterOption>;
  columns: typeof TABLE_COLUMNS;
  columnSizing: ColumnSizing;
  setIsEmptyDeals: (value: boolean) => void;
  refetch: () => void;
  onReorder: (columns: typeof TABLE_COLUMNS) => void;
  onColumnSizingChange: (columnSizing: ColumnSizing) => void;
};

const BuyerDeals: React.FC<BuyerDealsProps> = (props) => {
  const {
    className,
    dataLimit,
    isEmpty,
    isTableView,
    columnFilterOptions,
    columns,
    columnSizing,
    setIsEmptyDeals,
    refetch,
    onReorder,
    onColumnSizingChange,
  } = props;

  const classes = classNames('buyer-deals', 'resolve-page-items', className);

  const {
    isLoading,
    totalPages,
    totalItems,
    data: leads,
    setData,
    setIsLoading,
    setPaginatedData,
  } = useTableFetchState<PortfoilioLead>();

  const { popServerError } = useContext(PopNotificationsContext);

  const page = usePageParams();

  const searchParams = useSearchParams();
  const navigate = useNavigate();

  const onSubmitSuccess = useCallback(refetch, [refetch]);

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (lead: PortfoilioLead) =>
            BODY_CONTENT_GENERATE[col]({
              ...lead,
              config: {
                buyerStep: lead.buyerStep,
                withdrawnInterest: lead.withdrawnInterest,
                confirmedInterest: lead.confirmedInterest,
                leadId: lead._id,
                dealId: lead.dealRoom?._id,
                dealName: lead.dealRoom?.name,
                onSubmitSuccess,
              },
            }),
        }),
        {},
      ),
    [onSubmitSuccess],
  );

  const updateLead = useCallback(
    (lead: Partial<PortfoilioLead>) =>
      setData((old) =>
        old.map((el) => (el._id === lead._id ? { ...el, ...lead } : el)),
      ),
    [setData],
  );

  const renderPortfolioLeads = useMemo(
    () =>
      isLoading ? (
        <PortfolioPlaceholderCards numberOfCards={+dataLimit} />
      ) : (
        leads.map(
          ({
            _id: leadId,
            dealRoom: {
              _id,
              name,
              stage,
              listingStage,
              publishedOn,
              industry,
              dealOutcome,
              otherDealOutcome,
              portfolioName,
              isColleague,
              userId,
              user,
              isRWC,
              leadStagesCopies,
            },
            buyerStep,
            withdrawnInterest,
            confirmedInterest,
            progressedStep: leadProgressedStep,
            listingEnquiry: {
              _id: enquiryId,
              numberOfAcceptedLeads,
              numberOfConfirmedLeads,
              numberOfEnquiredLeads,
              numberOfOfferedLeads,
              progressedStep,
            },
            listing,
            listing: { type, expirationDate, seekingExpressionOfInterestDate },
          }) => (
            <ListingPortfolioCard
              asBuyer
              key={_id}
              title={portfolioName}
              dealStage={stage}
              listingStage={listingStage}
              date={publishedOn}
              isColleague={isColleague}
              dealOutcome={dealOutcome}
              otherDealOutcome={otherDealOutcome}
              dealRoomId={_id}
              dealName={name}
              listingType={type}
              industry={industry}
              seekingExpressionOfInterestDate={seekingExpressionOfInterestDate}
              expirationDate={expirationDate}
              ownerId={userId}
              owner={user}
              enquiryId={enquiryId}
              buyerStep={buyerStep}
              withdrawnInterest={withdrawnInterest}
              confirmedInterest={confirmedInterest}
              numberOfAcceptedLeads={numberOfAcceptedLeads}
              numberOfConfirmedLeads={numberOfConfirmedLeads}
              numberOfEnquiredLeads={numberOfEnquiredLeads}
              numberOfOfferedLeads={numberOfOfferedLeads}
              progressedStep={progressedStep}
              leadProgressedStep={leadProgressedStep}
              leadId={leadId}
              listing={listing}
              isRWC={isRWC}
              onWithdrawSuccess={refetch}
              leadStagesCopies={leadStagesCopies}
            />
          ),
        )
      ),
    [dataLimit, isLoading, leads, refetch],
  );

  const {
    $or,
    $sort,
    timeframe,
    from,
    to,
    ebitdaFrom,
    ebitdaTo,
    location,
    industry,
    listingStage,
    listingType,
  } = searchParams || {};

  const hasActiveFilters = useMemo(
    () =>
      !!(
        $or ||
        location ||
        industry ||
        from ||
        to ||
        ebitdaFrom ||
        ebitdaTo ||
        timeframe
      ),
    [$or, location, industry, from, to, ebitdaFrom, ebitdaTo, timeframe],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const priceRanges = getApiRangesFromParams(from, to);
        const ebitdaRanges = getApiRangesFromParams(ebitdaFrom, ebitdaTo);

        const { data } = await api.dealRoom.getPortfolioLeads({
          $or: $or || BUYING_ALL_LISTINGS_QUERY,
          $sort: $sort || BUYING_DEFAULT_SORT,
          $page: page,
          $limit: dataLimit,
          $relations:
            '["seller","listing","listingEnquiry","dealRoom","nudgeNotification"]',
          timeframe,
          priceRanges: safeStringify(priceRanges),
          ebitdaRanges: safeStringify(ebitdaRanges),
          location,
          industry,
          listingStage,
          listingType,
        });

        setPaginatedData(data);
        setIsEmptyDeals(getIsArrayEmpty(data.items));
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    isTableView,
    $or,
    $sort,
    timeframe,
    from,
    to,
    ebitdaFrom,
    ebitdaTo,
    location,
    industry,
    listingStage,
    listingType,
    page,
    dataLimit,
    popServerError,
    setIsEmptyDeals,
    setPaginatedData,
    setIsLoading,
  ]);

  if (isEmpty && !hasActiveFilters) return null;

  return (
    <div className={classes}>
      {!isTableView ? (
        <>
          {renderPortfolioLeads}
          <ResolvePagination totalPages={totalPages} onClick={scrollToTop} />
        </>
      ) : (
        <>
          <ReorderResizeTable
            className="portfolio-results-table"
            headingTranslations={HEADING_TRANSLATIONS}
            tableContentTransformations={renderedContent}
            columns={columns as Array<keyof PortfoilioLead>}
            sortingValues={SORTING_VALUES}
            columnFilterOptions={columnFilterOptions}
            isDataLoading={isLoading}
            data={leads}
            customSizes={CUSTOM_COL_SIZES}
            totalPages={totalPages}
            totalItems={totalItems}
            recordName="deal"
            getIsRowExpanded={({ original }) => !!original.nudgeNotification}
            onRowClick={(lead) => navigate(lead.listingEnquiry._id)}
            onReorder={
              onReorder as (columns: Array<keyof PortfoilioLead>) => void
            }
            columnSizing={columnSizing}
            onColumnSizingChange={onColumnSizingChange}
            onPaginationClick={scrollToTop}
            renderExpandedContent={(lead) => (
              <NudgeCellComponent
                _id={lead._id}
                nudgeNotification={lead.nudgeNotification}
                config={{ onSubmitSuccess, updateLead }}
              />
            )}
          />
        </>
      )}
    </div>
  );
};

export default BuyerDeals;
